'use client'

import { LandingPageContent } from '@/features/landing-page/landing-page-content'
import { usePathname } from 'next/navigation'
import { LANDING_PAGE_CUSTOM_CHILDREN_ROUTES } from '@/setup/routing/routing'
import {
    getLandingRouteContent,
    getLandingRouteContentV3,
} from '@/features/landing-page/helpers/landing-route-content'
import { isAuthBackdropRoute, isV3EligibleRoute } from '@/features/landing-page/landing-design'
import { useLandingDesign } from '@/features/landing-page/use-landing-design'
import { LpHeroSection } from '@/features/landing-page/v3/sections/lp-hero-section'
import { LpAgencyHeroNote } from '@/features/landing-page/v3/sections/lp-agency-callout'

export function LandingDefaultHeroSection() {
    const pathname = usePathname()
    const { effective } = useLandingDesign()

    const isCustomChildrenRoute = (LANDING_PAGE_CUSTOM_CHILDREN_ROUTES as readonly string[]).includes(pathname)
    if (isCustomChildrenRoute) return null

    // Non-backdrop auth routes (code-verification, sso-callback, oauth2, verify-invite, logout)
    // are transactional surfaces with a clean background — no marketing hero. Sign-in and sign-up
    // fall through and render the home hero behind the auth dialog (see isAuthBackdropRoute).
    if (pathname.startsWith('/auth') && !isAuthBackdropRoute(pathname)) return null

    // lp-v3 redesign: eligible pages use the new hero (per-page copy) when the design switch is on.
    // Sign-in/up render the home v3 hero as their backdrop.
    // (/pricing never reaches here — it's a custom-children route, caught above; its v3 hero lives
    // inside LpPricingSections.)
    if (effective === 'new' && (isV3EligibleRoute(pathname) || isAuthBackdropRoute(pathname))) {
        // On sign-in/up mirror the home hero exactly — V3_HOME_CONTENT copy + the agency note.
        // Without this, `/auth/sign-in` would resolve to DEFAULT_CONTENT (old "Full Ads..." copy)
        // because no V3 override is keyed on the auth pathname.
        const contentPathname = isAuthBackdropRoute(pathname) ? '/' : pathname
        const v3 = getLandingRouteContentV3(contentPathname)
        return (
            <LpHeroSection
                title={v3.title}
                subtitle={v3.subtitle}
                ctaLabel={v3.ctaLabel}
                belowCta={contentPathname === '/' ? <LpAgencyHeroNote /> : undefined}
            />
        )
    }

    const {
        title,
        subtitle,
        gradientSrc,
        ctaLabel,
        ctaHref,
        secondaryCtaLabel,
        secondaryCtaHref,
        bottomLogos,
        bottomLogoLabel,
        bottomTagline,
    } = getLandingRouteContent(pathname)

    return (
        <div className="w-full flex flex-col justify-center items-center">
            <div className="w-full max-w-[2400px] mx-auto px-4 pt-0 pb-8">
                <div className="w-full flex flex-col justify-center items-center">
                    <LandingPageContent
                        title={title}
                        subtitle={subtitle}
                        gradientSrc={gradientSrc}
                        ctaLabel={ctaLabel}
                        ctaHref={ctaHref}
                        secondaryCtaLabel={secondaryCtaLabel}
                        secondaryCtaHref={secondaryCtaHref}
                        bottomLogos={bottomLogos}
                        bottomLogoLabel={bottomLogoLabel}
                        bottomTagline={bottomTagline}
                    />
                </div>
            </div>
        </div>
    )
}
